import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Article } from '../services/api';
import { prefetchArticle } from '../hooks/usePrefetch';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
  priority?: boolean;
  /**
   * Heading level for the card title. The right level depends on what the card
   * is nested under, which only the caller knows: in a Related Articles block
   * the cards sit beneath an <h2>, so emitting <h2> made them its siblings in
   * the document outline. Defaults preserve the existing markup everywhere else.
   */
  headingLevel?: 2 | 3 | 4;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, variant = 'default', priority = false, headingLevel }) => {
  // `featured` and `default` shipped as h2, `compact` as h3. headingLevel overrides
  // both, so existing callers are byte-identical and only the caller that needs a
  // different level asks for one.
  const Heading = (`h${headingLevel ?? 2}`) as 'h2' | 'h3' | 'h4';
  const CompactHeading = (`h${headingLevel ?? 3}`) as 'h2' | 'h3' | 'h4';
  const handleMouseEnter = useCallback(() => {
    prefetchArticle(article.slug);
  }, [article.slug]);

  const getCategorySlug = (categoryName: string): string => {
    return categoryName
      .toLowerCase()
      .replace(/&/g, '-and-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  if (variant === 'featured') {
    return (
      <article className="group relative overflow-hidden rounded-xl" onMouseEnter={handleMouseEnter}>
        <Link to={`/article/${article.slug}/`} className="block">
          <div className="aspect-[16/9] overflow-hidden bg-slate-100">
            {article.featured_image ? (
              <img
                src={article.featured_image}
                srcSet={article.featured_image_srcset}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt={article.title}
                width={800}
                height={450}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading={priority ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={priority ? 'high' : 'auto'}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200" />
            )}
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5">
            {article.category_name && (
              <span className="inline-block px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded mb-2">
                {article.category_name}
              </span>
            )}
            <Heading className="text-lg md:text-xl font-bold text-white line-clamp-2 mb-1.5">
              {article.title}
            </Heading>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <span>{article.author_name}</span>
              <span>·</span>
              <time dateTime={article.published_at}>
                {format(new Date(article.published_at), 'MMM d, yyyy')}
              </time>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="flex gap-4 group">
        {article.featured_image && (
          <Link to={`/article/${article.slug}/`} className="flex-shrink-0">
            <img
              src={article.featured_image}
              srcSet={article.featured_image_srcset}
              sizes="128px"
              alt={article.title}
              width={128}
              height={72}
              className="w-32 aspect-video object-cover rounded-lg"
              loading="lazy"
              decoding="async"
            />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.slug}/`}>
            <CompactHeading className="text-sm font-medium text-slate-900 group-hover:text-blue-600 line-clamp-2 transition-colors">
              {article.title}
            </CompactHeading>
          </Link>
          <p className="text-xs text-slate-500 mt-1">
            {format(new Date(article.published_at), 'MMM d, yyyy')}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article
      className="group transition-transform duration-200 hover:-translate-y-1"
      onMouseEnter={handleMouseEnter}
    >
      {article.featured_image && (
        <Link to={`/article/${article.slug}/`} className="block mb-3">
          <div className="aspect-[16/10] overflow-hidden rounded-xl bg-slate-100 shadow-sm group-hover:shadow-md transition-shadow duration-200">
            <img
              src={article.featured_image}
              srcSet={article.featured_image_srcset}
              sizes={article.featured_image_sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px'}
              alt={article.title}
              width={article.featured_image_width || 400}
              height={article.featured_image_height || 250}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={priority ? 'high' : 'auto'}
            />
          </div>
        </Link>
      )}

      <div>
        {article.category_name && (
          <Link
            to={`/category/${getCategorySlug(article.category_name)}`}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {article.category_name}
          </Link>
        )}

        <Link to={`/article/${article.slug}/`}>
          <Heading className="mt-1.5 text-base md:text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2">
            {article.title}
          </Heading>
        </Link>

        {article.excerpt && (
          <p className="mt-2 text-sm text-slate-600 line-clamp-2">
            {article.excerpt}
          </p>
        )}

        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
          <span>{article.author_name}</span>
          <span>.</span>
          <time dateTime={article.published_at}>
            {format(new Date(article.published_at), 'MMM d, yyyy')}
          </time>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;
